import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

import { apiRoutes } from "../../configs/apiRoutes";
import instance from "../../api/apiClient";

export interface RoleItem {
  id: number;
  title: string;
  description?: string;
  isActive?: boolean;
  isSystem?: boolean;
  permissions?: {
    [key: string]: string[];
  };
}
interface RolesState {
  list: RoleItem[];
  item?: RoleItem;
  isListFetched: boolean;
}

interface RolePermissionPayload {
  role: string;
  section1: string;
  section2?: string;
  section3?: string;
  actionName: string;
  actionValue: number;
}

export interface RolePayload {
  title: string;
  description: string;
  isActive: boolean;
}

const getResponsePayload = (response: any) => response?.data ?? response;
const getListItems = (response: any): RoleItem[] => {
  const payload = getResponsePayload(response);
  return payload?.items ?? payload ?? [];
};

export const getRolesList = createAsyncThunk("roles/list", async () => {
  const result = await instance.get(
    `${apiRoutes.rolesList}?limit=100&offset=0`,
  );
  return getListItems(result);
});

export const getRolesItem = createAsyncThunk(
  "roles/item",
  async (id: number) => {
    const result = await instance.get(`${apiRoutes.roleItem}/${id}`);
    return getResponsePayload(result);
  },
);

export const createRole = createAsyncThunk(
  "roles/create",
  async (data: RolePayload, { rejectWithValue }) => {
    try {
      const result = await instance.post(apiRoutes.roleCreate, data);
      return getResponsePayload(result) as RoleItem;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const updateRole = createAsyncThunk(
  "roles/update",
  async (
    { id, data }: { id: number; data: RolePayload },
    { rejectWithValue },
  ) => {
    try {
      const result = await instance.patch(`${apiRoutes.roleUpdate}/${id}`, data);
      return getResponsePayload(result) as RoleItem;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const addRolesPermission = createAsyncThunk(
  "roles/add-permission",
  async (data: RolePermissionPayload) => {
    try {
      await instance.post(apiRoutes.roleAddPermission, { ...data });
      return true;
    } catch (error) {
      throw Error;
    }
  },
);

const initialState: RolesState = {
  list: [],
  isListFetched: false,
};

export const rolesSlice = createSlice({
  name: "roles",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(getRolesList.fulfilled, (state, action) => {
      state.isListFetched = true;
      state.list = action.payload ?? [];
    });
    // builder.addCase(addRolesPermission.fulfilled, (state) => {
    //   state.item = undefined;
    // });
    builder.addCase(getRolesItem.fulfilled, (state, action) => {
      const toLowercaseKeys = (obj: any) => {
        return Object.keys(obj).reduce((acc, key) => {
          // @ts-ignore
          acc[key?.toLowerCase()] = obj[key];
          return acc;
        }, {});
      };
      if (!action.payload) return;
      state.item = {
        ...action.payload,
        permissions: toLowercaseKeys(action.payload.permissions || {}),
      };
    });
  },
});

// export const { setIsAuthorized } = settingsSlice.actions;
